import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import {createFilterOptions, DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import {CheckboxForm} from "../../base/checkbox/checkbox";
import Api from "../../../../core/services/api/api";
import CKEditorTextfield from "../../text-fields/ck-editor-textfield";
import {deepEqual} from "../../../../core/services/utils/utils";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF} from '../../form';
import {useOperatorInformation} from "../../../hooks/use-operator-information";


const schema = Yup.object().shape({
    title: Yup.string().trim().nullable().required(ValidateMessages.required),
    body: Yup.string().trim().nullable().required(ValidateMessages.required),
    isPrivate: Yup.boolean().nullable().default(true),
});


const validate = makeValidate(schema);
const required = makeRequired(schema);

const filter = createFilterOptions();

const UpsertNoteDialog = () => {
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.upsertNote] ?? {});
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({
        title: '',
        body: '',
        isPrivate: state?.defaultIsPrivate ?? true,
    });
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const operatorInformation = useOperatorInformation();

    /**
     * With each change in note:
     * - sets the new values in initial values of the form.
     */
    useEffect(() => {
        if (state.open) {
            setInitialValues({
                title: state.note?.title.trim() ?? '',
                body: state.note?.body.trim() ?? '',
                isPrivate: state.note?.isPrivate ?? state.defaultIsPrivate ?? true,
            });
        }
    }, [state.note?.body, state.note?.isPrivate, state.note?.title, state.open, state.defaultIsPrivate])

    /**
     * Submits the changes and calls the onSuccess method of the redux state
     */
    const onSubmit = async (values) => {
        if (deepEqual(initialValues, values)) {
            closeDialog();
            return;
        }
        setLoading(true);
        /**@type {ApiResponseDs}
         */
        let response;
        if (state?.note) {
            // edit
            const forApi = {
                noteId: state?.note?.id,
                title: values.title ?? "",
                body: values.body ?? '',
                tagList: state?.note?.tagList ?? state?.tagList ?? [],
                isPrivate: values.isPrivate,
                updatedProperties: [
                    'Title',
                    'Body',
                    'IsPrivate',
                ]
            }
            response = await Api.updateNote(forApi);
            if (response?.resultFlag) {
                response.data = {
                    id: forApi.noteId,
                    title: forApi.title,
                    body: forApi.body,
                    tagList: forApi.tagList,
                    isPrivate: forApi.isPrivate,
                }
            }
        } else {
            // create
            const forApi = {
                title: values.title ?? "",
                body: values.body ?? '',
                isPrivate: values.isPrivate,
                tagList: state?.tagList ?? [],
            }
            response = await Api.createNote(forApi);
        }
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            closeDialog();
            state?.onSuccess?.({
                ...response.data,
                createdByName: operatorInformation?.name ?? "",
                createdBy: operatorInformation?.id,
                modifiedDateTime: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
            });
        }
    }

    /**
     * Closes the dialog by dispatching new state for the note dialog
     */
    const closeDialog = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {open: false}));
    }

    return (
        <>
            <Dialog
                open={state?.open}
                setOpen={closeDialog}
                maxWidth={'sm'}
                className={'upsert-note-dialog'}
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            !state?.note
                                ? "Create New Note"
                                : "Edit Note"
                        }
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2 pt-4'}>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={onSubmit}
                            id={'upsert-note-dialog-form'}
                            validate={validate}
                            initialValues={initialValues}
                            render={({values, form}) => {
                                return (
                                    <>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <MuiRFF.Autocomplete
                                                name={'title'}
                                                placeholder={'title...'}
                                                label={"Title"}
                                                required={required.title}
                                                options={state?.titleSuggestions ?? []}
                                                onBlur={e => {
                                                    if (e.target?.value?.trim())
                                                        form.change('title', e.target?.value?.trim());
                                                }}
                                                filterOptions={(options, params) => {
                                                    const filtered = filter(options, params);
                                                    if (params.inputValue !== '') {
                                                        filtered.push(params.inputValue);
                                                    }
                                                    return filtered;
                                                }}
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <CKEditorTextfield
                                                form
                                                name={'body'}
                                                required={required.body}
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <CheckboxForm
                                                name={'isPrivate'}
                                                required={required.isPrivate}
                                                data={{
                                                    value: values.isPrivate,
                                                    label: 'Private Note'
                                                }}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!state.open || loading}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => closeDialog()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={!state.open || loading}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'upsert-note-dialog-form'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {

                                    !state?.note
                                        ? loading ? "Creating..." : "Create"
                                        : loading ? "Updating..." : "Update"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default UpsertNoteDialog;
